import {PanelType, StackCompat as Stack} from 'components/FluentUICompat';
import {EntityListColumn, EntityPage} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import React from 'react';
import {TFunction, useTranslation} from 'react-i18next';
import {Market, UserRole} from 'types';
import {MarketFormElements} from './components';
import {
	GetAllMarketsDocument,
	useCreateMarketMutation,
	useDeleteMarketMutation,
	useGetAllMarketsQuery,
	useUpdateMarketMutation,
} from './hooks/markets.generated';
import {
	ENTITY_SUBFIELDS_KEY,
	MarketsTooltipTranslationProvider,
	ProviderThatEnablesGettingTooltipsFromContext,
	TOOLTIPS_TEXT_KEY,
} from 'features/localizedTooltips';
import {mapToRef, omitTypename} from 'helpers';
import {Control} from 'react-hook-form';
import {
	renderArrayField,
	renderTranslationField,
} from 'components/EntityList/ColumnRenderers';
import {numericalSort} from 'helpers/sortHelpers';
import {ARRAY_TYPE} from 'components/EntityList/EntityUtils';
import {useGetAllKeywordsQuery} from './hooks/keywords.generated';
import {useGetAllVkosQuery} from './hooks/vko.generated';
import {transcode} from 'buffer';

const AdminMarketsPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminMarketsPage',
	});
	const [createMarketMutation] = useCreateMarketMutation();
	const [updateMarketMutation] = useUpdateMarketMutation();
	const [deleteMarketMutation] = useDeleteMarketMutation();

	const {loading, data} = useGetAllMarketsQuery();
	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: dataVkos} = useGetAllVkosQuery();

	const markets: Market[] = React.useMemo(() => {
		const marketsUnsorted = data?.markets ?? [];
		return numericalSort(marketsUnsorted, 'sortPriority');
	}, [data]);

	const marketsWithEmptyKeywords = React.useMemo(() => {
		const keywordsAll = keywordData?.keywords ?? [];
		if (data && keywordData && dataVkos) {
			return markets.map(x => {
				const parentMarketIds = markets
					.filter(y => y.childMarkets.some(z => z.id === x.id))
					.map(y => y.id);
				const applicableMarketIds = [x.id, ...parentMarketIds];
				const marketVko =
					dataVkos.vkos?.filter(
						y =>
							y.markets.some(z => applicableMarketIds.includes(z.id)) ||
							y.markets.length === 0,
					) ?? [];
				const marketVkoIds = marketVko.map(y => y.vkoUser.id);
				const emptyKeywords = keywordsAll.filter(
					y => !y.vkos.some(z => marketVkoIds.includes(z.id)),
				);
				return {
					...x,
					emptyKeywords,
				};
			});
		}

		return [];
	}, [markets, keywordData, dataVkos]);

	const refetchQueries = [GetAllMarketsDocument];

	const createMarket = React.useCallback((market: Market) => {
		createMarketMutation({
			variables: {
				input: mapToInput(market),
			},
			refetchQueries,
		});
	}, []);
	const updateMarket = React.useCallback((market: Market) => {
		updateMarketMutation({
			variables: {
				input: mapToInput(market),
			},
			refetchQueries,
		});
	}, []);
	const deleteMarket = React.useCallback((id: string) => {
		deleteMarketMutation({
			variables: {
				input: {id},
			},
			refetchQueries,
		});
	}, []);

	const columns: EntityListColumn[] = React.useMemo(
		() => [
			{
				key: 'translations',
				name: t('Translations'),
				fieldName: 'translations',
				minWidth: 150,
				maxWidth: 150,
				isResizable: true,
				isMultiline: true,
				onRender: renderTranslationField(),
			},
			{
				key: 'childMarkets',
				name: t('ChildMarkets'),
				fieldName: 'childMarkets',
				minWidth: 150,
				maxWidth: 150,
				isResizable: true,
				isMultiline: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},

			{
				key: 'namingTemplates',
				name: t('namingTemplates'),
				fieldName: 'namingTemplates',
				minWidth: 50,
				maxWidth: 250,
				isResizable: true,
				onRender: renderNamingTemplates(t),
			},
			{
				key: 'countries',
				name: t('Countries'),
				fieldName: 'countries',
				minWidth: 200,
				maxWidth: 300,
				isResizable: true,
				isMultiline: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'emptyKeywords',
				name: t('EmptyKeywords'),
				fieldName: 'emptyKeywords',
				minWidth: 200,
				maxWidth: 300,
				isResizable: true,
				isMultiline: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'sortPriority',
				name: t('sortPriority'),
				fieldName: 'sortPriority',
				minWidth: 200,
				isResizable: true,
				isMultiline: true,
				filterable: true,
			},
		],
		[],
	);

	const getFormTranslationKeyPrefix = (): string => {
		return `${TOOLTIPS_TEXT_KEY}.${ENTITY_SUBFIELDS_KEY}`;
	};

	const formElements = React.useCallback(
		(control: Control<Market>) => {
			return (
				<MarketsTooltipTranslationProvider
					translationHookOptions={{keyPrefix: getFormTranslationKeyPrefix()}}
				>
					<ProviderThatEnablesGettingTooltipsFromContext>
						<MarketFormElements control={control} markets={markets} />
					</ProviderThatEnablesGettingTooltipsFromContext>
				</MarketsTooltipTranslationProvider>
			);
		},
		[markets],
	);

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<LoadWrapper loading={loading}>
				<MarketsTooltipTranslationProvider>
					<EntityPage
						items={marketsWithEmptyKeywords as any}
						entityDisplayName={t('Market')}
						createEntity={createMarket}
						updateEntity={updateMarket}
						deleteEntity={deleteMarket}
						additionalColumns={columns}
						panelType={PanelType.medium}
						renderAdditionalFormElements={formElements}
						hideSortPermanent={false}
					/>
				</MarketsTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

// eslint-disable-next-line react/display-name
const renderNamingTemplates = (t: TFunction) => (item: Market) => {
	const hasRegulationNamingTemplates =
		item.regulationNamingTemplates.length > 0;
	const hasRegulatoryDocumentNamingTemplates =
		item.regulatoryDocumentNamingTemplates.length > 0;

	return (
		<Stack horizontal tokens={{childrenGap: 8}}>
			<Stack>
				{hasRegulationNamingTemplates && (
					<Stack.Item>{t('RegulationNumber')}:</Stack.Item>
				)}
				{hasRegulatoryDocumentNamingTemplates && (
					<Stack.Item>{t('RegulatoryDocumentName')}:</Stack.Item>
				)}
			</Stack>
			<Stack>
				{hasRegulationNamingTemplates && (
					<Stack.Item>{item.regulationNamingTemplates.length}</Stack.Item>
				)}
				{hasRegulatoryDocumentNamingTemplates && (
					<Stack.Item>
						{item.regulatoryDocumentNamingTemplates.length}
					</Stack.Item>
				)}
			</Stack>
		</Stack>
	);
};

function mapToInput(market: Market) {
	return {
		id: market.id,
		name: market.name,
		childrenRefs: mapToRef((market as any).childMarkets),
		regulationNamingTemplates: omitTypename(market.regulationNamingTemplates),
		regulatoryDocumentNamingTemplates: omitTypename(
			market.regulatoryDocumentNamingTemplates,
		),
		countriesRef: mapToRef((market as any).countries),
		sortPriority: parseInt(`${market.sortPriority}`, 10),
		translations: (market.translations ?? []).map(x => omitTypename(x)),
	};
}

export default AdminMarketsPage;
